import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompanyBillingSummary,
  CompanySeatsSummary,
  CreateMunicipalInvitationDto,
  MunicipalInvitationSummary,
  MunicipalityOption,
  SeatEventSummary,
} from '@munifocus/contracts-gov';
import { api } from './api-client';

/**
 * A private enforcement company's seats, billing and municipal-administrator
 * invitations — the company administrator's side of the seat spec.
 *
 * Every write invalidates the whole `gov-admin` tree: a seat change moves the
 * "N of M" count, the employee list and the history at once, and the session
 * of the person who lost a seat.
 */

const base = (companyId: string) => `/administration/companies/${companyId}`;

export const seatKeys = {
  seats: (companyId: string) => ['gov-admin', 'companies', companyId, 'seats'] as const,
  history: (companyId: string) => ['gov-admin', 'companies', companyId, 'seat-history'] as const,
  billing: (companyId: string) => ['gov-admin', 'companies', companyId, 'billing'] as const,
  invitations: (companyId: string) => ['gov-admin', 'companies', companyId, 'invitations'] as const,
  municipalities: (companyId: string) =>
    ['gov-admin', 'companies', companyId, 'municipalities'] as const,
};

export function useCompanySeats(companyId: string | null | undefined) {
  return useQuery({
    queryKey: seatKeys.seats(companyId ?? ''),
    queryFn: async () =>
      (await api.get<CompanySeatsSummary>(`${base(companyId ?? '')}/seats`)).data,
    enabled: Boolean(companyId),
  });
}

export function useSeatHistory(companyId: string | null | undefined) {
  return useQuery({
    queryKey: seatKeys.history(companyId ?? ''),
    queryFn: async () =>
      (await api.get<SeatEventSummary[]>(`${base(companyId ?? '')}/seats/history`)).data,
    enabled: Boolean(companyId),
  });
}

export type SeatChange =
  | { action: 'assign'; membershipId: string }
  | { action: 'remove'; membershipId: string }
  | { action: 'move'; fromMembershipId: string; toMembershipId: string };

export function useChangeSeat(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (change: SeatChange) => {
      const { action, ...body } = change;
      return (await api.post<CompanySeatsSummary>(`${base(companyId)}/seats/${action}`, body)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

export function useCompanyBilling(companyId: string | null | undefined) {
  return useQuery({
    queryKey: seatKeys.billing(companyId ?? ''),
    queryFn: async () =>
      (await api.get<CompanyBillingSummary>(`${base(companyId ?? '')}/billing`)).data,
    enabled: Boolean(companyId),
  });
}

export function useMunicipalInvitations(companyId: string | null | undefined) {
  return useQuery({
    queryKey: seatKeys.invitations(companyId ?? ''),
    queryFn: async () =>
      (
        await api.get<MunicipalInvitationSummary[]>(
          `${base(companyId ?? '')}/municipal-invitations`,
        )
      ).data,
    enabled: Boolean(companyId),
  });
}

export function useMunicipalities(companyId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: seatKeys.municipalities(companyId ?? ''),
    queryFn: async () =>
      (await api.get<MunicipalityOption[]>(`${base(companyId ?? '')}/municipalities`)).data,
    enabled: Boolean(companyId) && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInviteMunicipalAdministrator(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateMunicipalInvitationDto) =>
      (await api.post<MunicipalInvitationSummary>(`${base(companyId)}/municipal-invitations`, dto))
        .data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}

export function useInvitationAction(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'resend' | 'cancel' }) =>
      (
        await api.post<MunicipalInvitationSummary>(
          `${base(companyId)}/municipal-invitations/${id}/${action}`,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gov-admin'] }),
  });
}
